import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import { extractTextFromMeterPhoto } from '../lib/meterOcr';
import { buyLabel, buyPath, isLowBalanceMeter } from '../lib/buyShortcut';
import {
  Zap, Droplets, Gauge, Wallet, AlertTriangle, Bell,
  Plus, TrendingUp, Loader2, ChevronDown, Activity, Camera, MessageSquare
} from "lucide-react";

const LIVE_POLL_MS = 30000;
// API rejects images over 3.5 MB; shrink big camera/gallery files before upload.
const OCR_MAX_UPLOAD_BYTES = Math.floor(3.5 * 1024 * 1024);
const OCR_MAX_EDGE_PX = 1600;

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const UTILITY_TYPES = new Set(["electricity", "water"]);

/** Downscale meter photos so uploads stay under the 3.5 MB API/nginx limit. */
async function prepareMeterPhoto(file) {
  if (!file || !file.type?.startsWith("image/")) return file;

  // Always recompress when over ~2 MB so typical phone shots land under 3.5 MB.
  if (file.size <= 2 * 1024 * 1024) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, OCR_MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    let quality = 0.85;
    let blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    while (blob && blob.size > OCR_MAX_UPLOAD_BYTES && quality > 0.5) {
      quality -= 0.1;
      blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    }
    if (!blob) return file;
    if (blob.size > OCR_MAX_UPLOAD_BYTES) {
      throw new Error("That photo is too large (max 3.5 MB). Try a closer shot of the meter display.");
    }
    if (blob.size >= file.size) {
      if (file.size > OCR_MAX_UPLOAD_BYTES) {
        throw new Error("That photo is too large (max 3.5 MB). Try a closer shot of the meter display.");
      }
      return file;
    }
    return new File([blob], (file.name || "meter").replace(/\.\w+$/, "") + ".jpg", {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    if (err instanceof Error && err.message.includes("too large")) throw err;
    return file;
  }
}

function meterKey(meterNumber, providerCode) {
  return `${meterNumber}_${providerCode}`;
}

function buildMeterOptions(accounts, providers, monitoredMeters) {
  const utilityCodes = new Set(
    providers.filter((p) => UTILITY_TYPES.has(p.type)).map((p) => p.code)
  );
  const options = new Map();

  const add = (meterNumber, providerCode, label) => {
    if (!meterNumber || !providerCode || !utilityCodes.has(providerCode)) return;
    const key = meterKey(meterNumber, providerCode);
    if (!options.has(key)) {
      options.set(key, { meter_number: meterNumber, provider_code: providerCode, label: label || meterNumber });
    }
  };

  accounts.forEach((a) => add(a.account_number, a.provider_code, a.nickname || a.account_number));
  (monitoredMeters || []).forEach((m) => add(m.meter_number, m.provider_code, m.meter_number));

  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function computeStatistics(historyData, period) {
  const readings = historyData?.history || [];
  const total_units_consumed = readings.reduce((sum, r) => sum + (r.units_used || 0), 0);
  const reading_count = readings.length;
  const days = period === "week" ? 7 : period === "month" ? 30 : 365;
  const average_daily_usage = days > 0 ? total_units_consumed / days : 0;
  return { total_units_consumed, average_daily_usage, reading_count };
}

// Decay the server-side projection locally so the balance visibly ticks
// between polls: projected - usage_per_hour * hours since the projection.
function tickedBalance(meter, nowMs) {
  const base = meter?.projected_balance_units ?? meter?.balance_units;
  if (base == null) return null;
  const projectedAt = meter?.projection_at ? Date.parse(meter.projection_at) : nowMs;
  const hours = Math.max((nowMs - projectedAt) / 3600000, 0);
  return Math.max(base - (meter?.usage_per_hour || 0) * hours, 0);
}

function formatDepletion(meter) {
  if (meter?.projected_days_remaining == null) return null;
  const days = meter.projected_days_remaining;
  if (days < 1) return "less than a day left";
  if (days < 2) return "~1 day left";
  return `~${Math.round(days)} days left`;
}

function formatUnits(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number.toFixed(2);
}

/** The number a successful scan should ask the user to confirm in one step. */
function scannedSummary(detected) {
  if (!detected) return null;
  const balance = formatUnits(detected.balance_units);
  const reading = formatUnits(detected.current_reading);
  if (!balance && !reading) return null;
  if (balance && (!reading || balance === reading)) {
    return { title: `${balance} units`, subtitle: "Prepaid balance read from the meter" };
  }
  if (reading && !balance) {
    return { title: reading, subtitle: "Cumulative reading" };
  }
  return { title: `${balance} units`, subtitle: `Prepaid balance · register ${reading}` };
}

function readingPayload(detectedOrForm, fromForm) {
  if (!fromForm) {
    const reading = detectedOrForm?.current_reading == null ? null : Number(detectedOrForm.current_reading);
    const balance = detectedOrForm?.balance_units == null ? null : Number(detectedOrForm.balance_units);
    const current = Number.isFinite(reading) ? reading : null;
    const units = Number.isFinite(balance) ? balance : null;
    if (current == null && units == null) return { error: "Enter a valid meter reading" };
    return { current_reading: current, balance_units: units };
  }
  const readingText = String(detectedOrForm.current_reading ?? "").trim();
  const balanceText = String(detectedOrForm.balance_units ?? "").trim();
  const reading = readingText === "" ? null : Number(readingText);
  const balance = balanceText === "" ? null : Number(balanceText);
  if (readingText !== "" && !Number.isFinite(reading)) return { error: "Enter a valid meter reading" };
  if (balanceText !== "" && !Number.isFinite(balance)) return { error: "Enter a valid prepaid balance" };
  if (reading == null && balance == null) {
    return { error: "Enter the cumulative reading, the prepaid balance, or both" };
  }
  return { current_reading: reading, balance_units: balance };
}

function freshnessText(meter) {
  if (!meter) return null;
  if (meter.estimate_label) return meter.estimate_label;
  if (meter.is_estimate) return "Estimated";
  return null;
}

export default function RealTimeMonitoringPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [providers, setProviders] = useState([]);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectingMeter, setSelectingMeter] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [meterHistory, setMeterHistory] = useState(null);
  const [historyPeriod, setHistoryPeriod] = useState("week");
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [alertForm, setAlertForm] = useState({ alert_type: "low_balance", threshold: 50 });
  const [liveSummary, setLiveSummary] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const liveMetersRef = useRef(new Map());
  const seenAlertIdsRef = useRef(new Set());
  const fileInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrForm, setOcrForm] = useState({ current_reading: "", balance_units: "" });
  const [ocrEditing, setOcrEditing] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const [ocrCanOverride, setOcrCanOverride] = useState(false);
  const [confirmingReading, setConfirmingReading] = useState(false);
  const [scanPrompt, setScanPrompt] = useState(false);
  const [showSmsPaste, setShowSmsPaste] = useState(false);
  const [smsText, setSmsText] = useState("");
  const [importingSms, setImportingSms] = useState(false);
  const scanLinkHandled = useRef("");
  const beginScanRef = useRef(async () => {});
  const pendingCamera = useRef(false);
  const pendingScanKey = useRef("");

  const meterOptions = useMemo(
    () => buildMeterOptions(savedAccounts, providers, dashboard?.meters),
    [savedAccounts, providers, dashboard?.meters]
  );

  const selectedMeterKey = selectedMeter
    ? meterKey(selectedMeter.meter_number, selectedMeter.provider_code)
    : "";

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Live loop: poll the projection endpoint, and tick the display each second
  useEffect(() => {
    const poll = setInterval(() => {
      fetchLiveMeters();
      fetchMeterAlerts();
    }, LIVE_POLL_MS);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    fetchLiveMeters();
    fetchMeterAlerts();
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  const openScanNotification = (url) => {
    if (!url) return;
    const current = `${window.location.pathname}${window.location.search}`;
    if (current === url) {
      const params = new URLSearchParams(window.location.search);
      beginScanRef.current(params.get("meter"), params.get("provider"));
      return;
    }
    scanLinkHandled.current = "";
    navigate(url);
  };

  const fetchMeterAlerts = async () => {
    try {
      const response = await api.get("/notifications", { params: { unread_only: true, limit: 10 } });
      const items = response.data?.notifications || [];
      for (const item of items) {
        if (item.type !== "meter_alert" || seenAlertIdsRef.current.has(item.id)) continue;
        seenAlertIdsRef.current.add(item.id);
        const alertType = item.data?.alert_type;
        const actionUrl = (alertType === "scan_nudge" || alertType === "low_balance") ? item.data?.url : null;
        const actionLabel = alertType === "scan_nudge" ? "Scan" : (item.data?.buy_label || "Buy units");
        toast.warning(item.message, {
          description: item.title,
          action: actionUrl ? { label: actionLabel, onClick: () => openScanNotification(actionUrl) } : undefined,
        });
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const notification = new Notification(item.title, { body: item.message, tag: item.id });
          if (actionUrl) {
            notification.onclick = () => {
              window.focus();
              window.location.assign(actionUrl);
            };
          }
        }
      }
    } catch (error) {
      console.error("Meter alert poll failed:", error);
    }
  };

  const fetchLiveMeters = async () => {
    try {
      const response = await api.get("/monitoring/meters/live");
      const meters = response.data?.meters || [];
      const map = new Map(meters.map((m) => [meterKey(m.meter_number, m.provider_code), m]));
      liveMetersRef.current = map;
      setLiveSummary(response.data?.summary || null);
      setSelectedMeter((current) => {
        if (!current) return current;
        const fresh = map.get(meterKey(current.meter_number, current.provider_code));
        return fresh ? { ...current, ...fresh } : current;
      });
    } catch (error) {
      // Keep showing the last projection; next poll will retry
      console.error("Live meter poll failed:", error);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardRes, accountsRes, providersRes] = await Promise.all([
        api.get("/monitoring/dashboard"),
        api.get("/accounts"),
        api.get("/providers"),
      ]);
      setDashboard(dashboardRes.data);
      setSavedAccounts(accountsRes.data || []);
      setProviders(providersRes.data || []);
    } catch (err) {
      console.error("Failed to load monitoring data:", err);
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    const response = await api.get("/monitoring/dashboard");
    setDashboard(response.data);
    return response.data;
  };

  const submitTokenSms = async () => {
    const text = smsText.trim();
    if (!text) {
      toast.error("Paste the ESCOM token SMS");
      return;
    }
    setImportingSms(true);
    try {
      const response = await api.post("/monitoring/escom/sms", { text });
      const parsed = response.data?.parsed || {};
      const meter = parsed.meter_number;
      if (response.data?.duplicate) {
        toast.message(`That token for meter ${meter} was already imported`);
      } else {
        toast.success(`Added ${parsed.units_kwh} kWh to meter ${meter}`);
      }
      setSmsText("");
      setShowSmsPaste(false);
      const dashboardData = await fetchDashboard();
      await fetchLiveMeters();
      const digits = String(meter || "").replace(/\D/g, "");
      const match = (dashboardData?.meters || []).find(
        (m) => String(m.meter_number || "").replace(/\D/g, "") === digits
      );
      if (match) {
        setSelectedMeter(match);
        await fetchMeterHistory(match);
      }
    } catch (err) {
      toast.error(classifyError(err).message || "Could not import that SMS");
    } finally {
      setImportingSms(false);
    }
  };

  const fetchMeterHistory = async (meter, period = historyPeriod) => {
    if (!meter) return;
    try {
      const response = await api.get(
        `/monitoring/meters/${meter.meter_number}/history?provider_code=${meter.provider_code}&period=${period}`
      );
      setMeterHistory({
        ...response.data,
        statistics: computeStatistics(response.data, period),
      });
    } catch (err) {
      console.error("Failed to load meter history:", err);
      setMeterHistory(null);
      toast.error(`Could not load usage history: ${classifyError(err).message}`);
    }
  };

  const handleMeterSelect = async (key) => {
    if (!key) {
      setSelectedMeter(null);
      setMeterHistory(null);
      return;
    }

    const option = meterOptions.find((m) => meterKey(m.meter_number, m.provider_code) === key);
    if (!option) return;

    setSelectingMeter(true);
    try {
      const isMonitored = dashboard?.meters?.some(
        (m) => meterKey(m.meter_number, m.provider_code) === key
      );

      let data = dashboard;
      if (!isMonitored) {
        await api.post("/monitoring/meters/add", null, {
          params: { meter_number: option.meter_number, provider_code: option.provider_code },
        });
        data = await fetchDashboard();
        toast.success("Meter added for monitoring");
      }

      const meter =
        data?.meters?.find((m) => meterKey(m.meter_number, m.provider_code) === key) || option;

      setSelectedMeter(meter);
      await fetchMeterHistory(meter);
    } catch (err) {
      console.error("Failed to select meter:", err);
      toast.error(`Could not start monitoring this meter: ${classifyError(err).message}`);
    } finally {
      setSelectingMeter(false);
    }
  };

  beginScanRef.current = async (meterNumber, providerCode) => {
    if (!meterNumber || !providerCode) return;
    pendingScanKey.current = meterKey(meterNumber, providerCode);
    pendingCamera.current = true;
    setScanPrompt(true);
    await handleMeterSelect(pendingScanKey.current);
  };

  useEffect(() => {
    if (!pendingCamera.current || !selectedMeter) return;
    const key = meterKey(selectedMeter.meter_number, selectedMeter.provider_code);
    if (pendingScanKey.current && key !== pendingScanKey.current) return;
    pendingCamera.current = false;
    fileInputRef.current?.click();
  }, [selectedMeter, scanPrompt]);

  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    if (params.get("scan") !== "1") return;
    const meterNumber = params.get("meter");
    const providerCode = params.get("provider");
    if (!meterNumber || !providerCode) return;
    const token = `${meterNumber}|${providerCode}|${location.search}`;
    if (scanLinkHandled.current === token) return;
    scanLinkHandled.current = token;
    beginScanRef.current(meterNumber, providerCode);
  }, [loading, location.search]);

  const createAlert = async () => {
    if (!selectedMeter) {
      toast.error("Select a meter first");
      return;
    }
    try {
      await api.post("/monitoring/alerts", {
        meter_number: selectedMeter.meter_number,
        ...alertForm
      });
      toast.success("Alert created successfully");
      setShowAddAlert(false);
      await fetchDashboard();
    } catch (err) {
      toast.error(classifyError(err).message);
    }
  };

  // Bounds the scan so the button can never spin forever — if the backend
  // or the vision model hangs, the request fails fast with a clear message
  // instead of leaving "Scanning…" on screen indefinitely.
  const OCR_REQUEST_TIMEOUT_MS = 30000;

  const handleScanPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedMeter) return;

    setScanning(true);
    try {
      const photo = await prepareMeterPhoto(file);
      // Browser OCR fallback (like Flutter ML Kit) — helps when the server
      // has no GOOGLE_API_KEY for Gemini Vision.
      const extractedText = await extractTextFromMeterPhoto(photo);
      const form = new FormData();
      form.append("file", photo);
      form.append("provider_code", selectedMeter.provider_code);
      if (extractedText) form.append("extracted_text", extractedText);
      const response = await api.post(
        `/monitoring/meters/${selectedMeter.meter_number}/ocr`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: OCR_REQUEST_TIMEOUT_MS,
        }
      );
      const detected = response.data?.detected || {};
      setOcrResult(response.data);
      setOcrForm({
        current_reading: detected.current_reading != null ? String(detected.current_reading) : "",
        balance_units: detected.balance_units != null ? String(detected.balance_units) : "",
      });
      setOcrEditing(!response.data?.success || !scannedSummary(detected));
      setOcrError(null);
      setOcrCanOverride(false);
      if (response.data?.ocr_message) {
        toast.message(response.data.ocr_message);
      } else if (!response.data?.success) {
        toast.message("Could not auto-read the display — enter the values manually.");
      }
    } catch (err) {
      if (err.code === "ECONNABORTED") {
        toast.error("The photo scan took too long. Try a clearer, well-lit photo or enter the reading manually.");
      } else {
        setOcrResult({
          success: false,
          detected: { method: "manual", current_reading: null, balance_units: null },
        });
        setOcrForm({ current_reading: "", balance_units: "" });
        setOcrEditing(true);
        setOcrError(null);
        setOcrCanOverride(false);
        toast.error(classifyError(err).message || "Could not read meter photo — enter readings manually.");
      }
    } finally {
      setScanning(false);
    }
  };

  const confirmOcrReading = async (acknowledgeAnomaly = false) => {
    if (!selectedMeter) return;
    const parsed = ocrEditing
      ? readingPayload(ocrForm, true)
      : readingPayload(ocrResult?.detected, false);
    if (parsed.error) {
      setOcrError(parsed.error);
      setOcrEditing(true);
      return;
    }

    setConfirmingReading(true);
    setOcrError(null);
    try {
      await api.post(`/monitoring/meters/${selectedMeter.meter_number}/reading/confirm`, {
        provider_code: selectedMeter.provider_code,
        current_reading: parsed.current_reading,
        balance_units: parsed.balance_units,
        acknowledge_anomaly: acknowledgeAnomaly,
      });
      toast.success("Meter reading saved");
      setOcrResult(null);
      setScanPrompt(false);
      await fetchLiveMeters();
      await fetchDashboard();
      await fetchMeterHistory(selectedMeter);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const structured = detail && typeof detail === "object" && !Array.isArray(detail) ? detail : null;
      const message = structured?.message || classifyError(err).message || "Failed to save reading";
      setOcrError(message);
      setOcrCanOverride(Boolean(structured?.can_override));
      setOcrEditing(true);
    } finally {
      setConfirmingReading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchInitialData} />;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="monitoring-dashboard">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">How much power you're using</h1>
          <p className="text-muted-foreground">Choose a meter to track usage, balance, and alerts</p>
        </div>
        {selectedMeter?.is_estimate ? (
          <span className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 text-xs font-medium">
            ESTIMATE
          </span>
        ) : (
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            LIVE
          </span>
        )}
      </div>

      {scanPrompt && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
          <p className="font-medium">Scan this meter</p>
          <p className="text-xs text-muted-foreground mt-1">
            The balance is only an estimate until you photograph the display.
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-2 text-sm font-medium text-emerald-700 hover:underline"
          >
            Open camera
          </button>
        </div>
      )}

      {/* Meter selector */}
      <div className="bg-card border border-border/50 rounded-xl p-4">
        <label htmlFor="monitor-meter-select" className="block text-sm font-medium mb-2">
          Meter to monitor
        </label>
        <div className="relative">
          <select
            id="monitor-meter-select"
            value={selectedMeterKey}
            onChange={(e) => handleMeterSelect(e.target.value)}
            disabled={selectingMeter || meterOptions.length === 0}
            className="w-full appearance-none p-3 pr-10 bg-background border border-border/50 rounded-lg focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
            data-testid="monitor-meter-select"
          >
            <option value="">
              {meterOptions.length === 0 ? "No meters available — save a meter in Pay Bill first" : "Select a meter…"}
            </option>
            {meterOptions.map((m) => {
              const key = meterKey(m.meter_number, m.provider_code);
              return (
                <option key={key} value={key}>
                  {m.label} ({m.provider_code})
                </option>
              );
            })}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
        </div>
        {selectingMeter && (
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Setting up monitoring…
          </p>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Gauge className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monitored Meters</p>
              <p className="text-xl font-bold">{dashboard?.summary?.total_meters || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Wallet className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Balance (units)</p>
              <p className="text-xl font-bold">
                {((liveSummary?.total_balance_units ?? dashboard?.summary?.total_balance_units) || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Low Balance</p>
              <p className="text-xl font-bold">{dashboard?.summary?.low_balance_count || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Alerts</p>
              <p className="text-xl font-bold">{dashboard?.alerts?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Meter details */}
      <div className="space-y-4">
        {selectedMeter ? (
          <>
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${selectedMeter.provider_code?.toLowerCase().includes("escom") ? "bg-yellow-500/10" : "bg-blue-500/10"}`}>
                  {selectedMeter.provider_code?.toLowerCase().includes("escom")
                    ? <Zap className="w-5 h-5 text-yellow-500" />
                    : <Droplets className="w-5 h-5 text-blue-500" />}
                </div>
                <div>
                  <p className="font-semibold">{selectedMeter.meter_number}</p>
                  <p className="text-sm text-muted-foreground">{selectedMeter.provider_code}</p>
                </div>
                <div className="ml-auto text-right text-sm max-w-[16rem]">
                  <p className="text-muted-foreground flex items-center justify-end gap-1">
                    <Activity className={`w-3 h-3 ${selectedMeter.is_estimate ? "text-amber-500" : "text-emerald-500"}`} />
                    {selectedMeter.is_estimate ? "Estimated balance" : "Balance"}
                  </p>
                  <p className="font-semibold text-base tabular-nums">
                    {(tickedBalance(selectedMeter, nowMs) ?? 0).toFixed(2)} units
                  </p>
                  {freshnessText(selectedMeter) && (
                    <p className="text-xs text-muted-foreground" data-testid="estimate-label">
                      {freshnessText(selectedMeter)}
                    </p>
                  )}
                  {formatDepletion(selectedMeter) && (
                    <p className="text-xs text-muted-foreground">{formatDepletion(selectedMeter)}</p>
                  )}
                </div>
              </div>

              {isLowBalanceMeter(
                { ...selectedMeter, projected_balance_units: tickedBalance(selectedMeter, nowMs) ?? selectedMeter.projected_balance_units },
                dashboard?.alerts,
              ) && (
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 rounded-xl border border-orange-500/40 bg-orange-500/10" data-testid="low-balance-warning">
                  <p className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                    {selectedMeter.meter_mode === "postpaid"
                      ? "Low balance — pay this bill before it lapses."
                      : "Low balance — top up before the units run out."}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(buyPath(selectedMeter))}
                    className="px-3 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                    data-testid="buy-units-btn"
                  >
                    {buyLabel(selectedMeter)}
                  </button>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-border/40">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.heic,.heif,image/heic,image/heif"
                  capture="environment"
                  className="hidden"
                  onChange={handleScanPhoto}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanning}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-50"
                >
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  {scanning ? "Scanning…" : "Scan meter photo"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSmsPaste(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-muted hover:bg-muted/80"
                  data-testid="paste-token-sms"
                >
                  <MessageSquare className="w-4 h-4" />
                  Paste token SMS
                </button>
                <p className="text-xs text-muted-foreground">Snap the display, or paste an ESCOM token text bought outside the app</p>
              </div>

              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold">Usage Statistics</h2>
                <div className="flex gap-2">
                  {["week", "month", "year"].map((period) => (
                    <button
                      key={period}
                      onClick={() => {
                        setHistoryPeriod(period);
                        fetchMeterHistory(selectedMeter, period);
                      }}
                      className={`px-3 py-1 text-sm rounded-lg transition-all ${historyPeriod === period ? "bg-emerald-500 text-white" : "bg-muted hover:bg-muted/80"}`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-500">{meterHistory?.statistics?.total_units_consumed?.toFixed(1) || 0}</p>
                  <p className="text-xs text-muted-foreground">Units Consumed</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-blue-500">{meterHistory?.statistics?.average_daily_usage?.toFixed(1) || 0}</p>
                  <p className="text-xs text-muted-foreground">Daily Average</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{meterHistory?.statistics?.reading_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Readings</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border/50 rounded-xl p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold">Alerts</h2>
                <button onClick={() => setShowAddAlert(true)} className="flex items-center gap-1 text-sm text-emerald-500 hover:text-emerald-400">
                  <Plus className="w-4 h-4" /> Add Alert
                </button>
              </div>
              {dashboard?.alerts?.filter((a) => a.meter_number === selectedMeter.meter_number).length === 0 ? (
                <p className="text-muted-foreground text-sm">No alerts configured for this meter</p>
              ) : (
                <div className="space-y-2">
                  {dashboard?.alerts?.filter((a) => a.meter_number === selectedMeter.meter_number).map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between gap-2 p-2 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        {alert.alert_type === "low_balance" ? <AlertTriangle className="w-4 h-4 text-orange-500" /> : <TrendingUp className="w-4 h-4 text-red-500" />}
                        <span className="text-sm">{alert.alert_type === "low_balance" ? "Low Balance" : "High Usage"} Alert</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Threshold: {alert.threshold}</span>
                        {alert.alert_type === "low_balance" && (
                          <button
                            type="button"
                            onClick={() => navigate(buyPath(selectedMeter))}
                            className="px-2 py-1 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                            data-testid="buy-units-alert-btn"
                          >
                            {buyLabel(selectedMeter)}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-card border border-border/50 rounded-xl p-8 text-center">
            <Gauge className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Select a meter above to view usage and alerts</p>
            <button
              type="button"
              onClick={() => setShowSmsPaste(true)}
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-muted hover:bg-muted/80"
              data-testid="paste-token-sms"
            >
              <MessageSquare className="w-4 h-4" />
              Paste token SMS
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAddAlert && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Create Alert</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Alert Type</label>
                  <select value={alertForm.alert_type} onChange={(e) => setAlertForm({ ...alertForm, alert_type: e.target.value })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg">
                    <option value="low_balance">Low Balance</option>
                    <option value="high_usage">High Usage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Threshold (units)</label>
                  <input type="number" value={alertForm.threshold} onChange={(e) => setAlertForm({ ...alertForm, threshold: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddAlert(false)} className="flex-1 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all">Cancel</button>
                <button onClick={createAlert} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all">Create Alert</button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {showSmsPaste && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-2">Paste token SMS</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Paste the full ESCOM purchase text. Units bought outside the app are added to that meter. The same token on the same day is imported once.
              </p>
              <textarea
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                rows={8}
                placeholder="ESCOM Prepaid…"
                className="w-full p-3 bg-background border border-border/50 rounded-lg text-sm"
                data-testid="token-sms-text"
              />
              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSmsPaste(false)}
                  className="flex-1 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitTokenSms}
                  disabled={importingSms}
                  className="flex-1 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50"
                >
                  {importingSms ? "Importing…" : "Import token"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {ocrResult && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-2">Confirm meter reading</h3>
              {!ocrEditing && scannedSummary(ocrResult.detected) ? (
                <div className="mb-4 rounded-xl bg-muted/40 px-4 py-5 text-center">
                  <p className="text-3xl font-bold tabular-nums">{scannedSummary(ocrResult.detected).title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{scannedSummary(ocrResult.detected).subtitle}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-4">
                  {ocrResult.success
                    ? "Correct the number, then save."
                    : "We could not auto-read the display — type the values from the meter, then save."}
                </p>
              )}
              {ocrEditing && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Cumulative reading (kWh)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={ocrForm.current_reading}
                      onChange={(e) => setOcrForm({ ...ocrForm, current_reading: e.target.value })}
                      className="w-full p-2 bg-background border border-border/50 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Prepaid balance (units)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={ocrForm.balance_units}
                      onChange={(e) => setOcrForm({ ...ocrForm, balance_units: e.target.value })}
                      className="w-full p-2 bg-background border border-border/50 rounded-lg"
                    />
                  </div>
                </div>
              )}
              {ocrError && (
                <p className="mt-4 text-sm text-red-600" role="alert">{ocrError}</p>
              )}
              <div className="flex flex-col gap-2 mt-6">
                <button
                  onClick={() => confirmOcrReading(false)}
                  disabled={confirmingReading}
                  className="w-full py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50"
                >
                  {confirmingReading
                    ? "Saving…"
                    : ocrEditing
                      ? "Save corrected reading"
                      : `Confirm ${scannedSummary(ocrResult.detected)?.title || "reading"}`}
                </button>
                {!ocrEditing && (
                  <button
                    type="button"
                    onClick={() => { setOcrEditing(true); setOcrError(null); }}
                    className="w-full py-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    That's not right
                  </button>
                )}
                {ocrCanOverride && (
                  <button
                    type="button"
                    onClick={() => confirmOcrReading(true)}
                    disabled={confirmingReading}
                    className="w-full py-2 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-50"
                  >
                    Save anyway
                  </button>
                )}
                <button onClick={() => setOcrResult(null)} className="w-full py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
